/**
 * Canonical helpers for QR export downloads (variant image API returns JSON with data URLs).
 * Using Blob + object URL avoids huge data: anchors, reduces macOS Finder timestamp quirks,
 * and validates payload before download.
 */

/**
 * Resolve public API base (no trailing slash).
 * Never returns the literal string "undefined" (invalid CRA env); falls back to window.location.origin.
 */
export function getBackendBaseUrl() {
  const env = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");
  if (env && env !== "undefined") {
    return env;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "";
}

/**
 * Fetch PNG/SVG payload for export modal options (same contract as /variants/:id/image and /qr-preview).
 */
export async function fetchVariantExportPayload(api, variant, option) {
  const sd = variant.style || {};
  const baseUrl = getBackendBaseUrl();
  const params = new URLSearchParams({
    format: option.format,
    size: option.size.toString(),
    base_url: baseUrl,
  });

  if (option.engraving) {
    const res = await api.post("/qr-preview", {
      qr_preset: "engraving",
      engraving_safe: true,
      corner_style: "square",
    });
    return res.data;
  }

  const res = await api.get(`/variants/${variant.id}/image?${params}`);
  return res.data;
}

/**
 * @param {string} dataUrl - data:image/png;base64,...
 * @param {string} filename
 */
export function downloadPngFromDataUrl(dataUrl, filename) {
  if (!dataUrl || typeof dataUrl !== "string") {
    throw new Error("Missing PNG data");
  }
  const trimmed = dataUrl.trim();
  const m = /^data:image\/png;base64,(.+)$/i.exec(trimmed);
  if (!m) {
    throw new Error("Response is not a PNG data URL");
  }
  let binary;
  try {
    binary = atob(m[1]);
  } catch {
    throw new Error("Invalid PNG base64 payload");
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Download PNG from GET /variants/:id/image style response body.
 */
export function downloadPngFromVariantImageResponse(resData, filename) {
  if (!resData || resData.format !== "png" || !resData.data) {
    throw new Error("Invalid variant image response");
  }
  downloadPngFromDataUrl(resData.data, filename);
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function downloadSvgString(svgMarkup, filename) {
  if (!svgMarkup || typeof svgMarkup !== "string" || !svgMarkup.includes("<svg")) {
    throw new Error("Missing SVG markup");
  }
  triggerBlobDownload(
    new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" }),
    filename || "attendance-qr.svg",
  );
}

export function downloadPdfFromDataUrl(dataUrl, filename) {
  const match = /^data:application\/pdf;base64,(.+)$/i.exec((dataUrl || "").trim());
  if (!match) throw new Error("Response is not a PDF data URL");
  const binary = atob(match[1]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  triggerBlobDownload(new Blob([bytes], { type: "application/pdf" }), filename || "attendance-qr.pdf");
}

/** Download any validated base64 export returned by the variant image API. */
export function downloadVariantDataUrl(resData, filename) {
  const allowed = {
    png: "image/png",
    jpeg: "image/jpeg",
    webp: "image/webp",
    pdf: "application/pdf",
  };
  const mime = allowed[resData?.format];
  if (!mime || !resData?.data) {
    throw new Error("Invalid QR export response");
  }
  const match = new RegExp(`^data:${mime.replace("/", "\\/")};base64,(.+)$`, "i").exec(resData.data.trim());
  if (!match) {
    throw new Error(`Response is not a valid ${resData.format.toUpperCase()} export`);
  }
  const binary = atob(match[1]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
